import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Shield, Mail, Lock, Loader2, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Use XMLHttpRequest to avoid "body stream already read" errors
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_URL}/api/admin/login`, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    
    xhr.onreadystatechange = function() {
      if (xhr.readyState === 4) {
        setLoading(false);
        
        if (xhr.status === 200) {
          try {
            const data = JSON.parse(xhr.responseText);
            localStorage.setItem('admin_token', data.access_token);
            localStorage.setItem('token', data.access_token);
            localStorage.setItem('admin_user', JSON.stringify(data.admin));
            window.location.href = '/admin/dashboard';
          } catch (err) {
            setError('Erreur de parsing de la réponse');
          }
        } else {
          try {
            const data = JSON.parse(xhr.responseText);
            setError(data.detail || 'Identifiants invalides');
          } catch (err) {
            setError('Identifiants invalides');
          }
        }
      }
    };
    
    xhr.onerror = function() {
      setLoading(false);
      setError('Erreur de connexion au serveur');
    };
    
    xhr.send(JSON.stringify({ 
      email: email.trim(), 
      password: password 
    }));
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-20 px-4 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-6">
            <div className="w-14 h-14 rounded-xl bg-red-600 flex items-center justify-center shadow-lg shadow-red-600/30">
              <Shield className="w-7 h-7 text-white" />
            </div>
            <div className="text-left">
              <span className="font-heading text-2xl font-bold text-white block">AFY</span>
              <span className="text-xs text-red-400 font-medium uppercase tracking-wider">Administration</span>
            </div>
          </div>
          <h1 className="font-heading text-3xl font-semibold mb-2 text-white">
            Portail Admin
          </h1>
          <p className="text-slate-400">
            Accès sécurisé réservé aux administrateurs
          </p>
        </div>

        <Card className="border-slate-700 bg-slate-800/50 backdrop-blur shadow-2xl">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg text-slate-300 flex items-center gap-2">
              <Lock className="w-5 h-5 text-red-500" />
              Authentification Administrateur
            </CardTitle>
          </CardHeader>
          <CardContent>
            {error && (
              <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 flex items-center gap-2 text-red-400" data-testid="admin-login-error">
                <AlertCircle className="w-5 h-5 shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-300">Email Admin</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="admin@afy.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-12 bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-red-500 focus:ring-red-500/20"
                    required
                    autoComplete="email"
                    data-testid="admin-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-300">Mot de passe</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="Entrez votre mot de passe"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 h-12 bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-red-500 focus:ring-red-500/20"
                    required
                    autoComplete="current-password"
                    data-testid="admin-password-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 bg-red-600 hover:bg-red-700 text-white font-semibold mt-6"
                disabled={loading}
                data-testid="admin-login-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Authentification...
                  </>
                ) : (
                  <>
                    <Shield className="w-4 h-4 mr-2" />
                    Accéder au Panel Admin
                  </>
                )}
              </Button>
            </form>

            <div className="mt-6 pt-4 border-t border-slate-700">
              <p className="text-xs text-slate-500 text-center">
                Ce portail est réservé aux administrateurs autorisés uniquement.
              </p>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-slate-500 mt-6 text-sm">
          Pas administrateur ?{' '}
          <a href="/login" className="text-red-400 hover:underline">
            Retour à la connexion principale
          </a>
        </p>
      </div>
    </div>
  );
}
